import React from 'react';
import { Plane, Ship } from 'lucide-react';
import { Label } from '../../ui/label';
import GeographicSelector from '../../GeographicSelector';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useGeographicData } from '../../../hooks/useGeographicData';
import { TRANSPORT_MODE_LABELS } from '../../../lib/constants';

/** Sélecteur de ville seul — évite un second menu pays. */
export const DestinationCitySelect = ({ selectedCountry, selectedCity, onCityChange }) => {
  const { t } = useLanguage();
  const { countries, getCitiesForCountry } = useGeographicData();

  // Le formulaire QR stocke des noms de pays, le dashboard des codes.
  const resolvedCountry = React.useMemo(() => {
    if (!selectedCountry || !countries.length) return selectedCountry;
    const match = countries.find((c) => c.code === selectedCountry || c.name === selectedCountry);
    return match?.code || selectedCountry;
  }, [selectedCountry, countries]);

  const cities = resolvedCountry ? getCitiesForCountry(resolvedCountry) : [];
  const cityInList = selectedCity && cities.some((c) => c.name === selectedCity);

  if (!resolvedCountry || cities.length === 0) {
    return (
      <div className="space-y-2">
        <Label>🏙️ {t('destinationCityLabel')} *</Label>
        <select
          disabled
          className="w-full border border-border rounded-md px-3 py-2 text-sm text-muted-foreground bg-muted/50"
        >
          <option>
            {resolvedCountry
              ? t('noCitiesAvailable') || 'Aucune ville disponible'
              : t('selectCountryFirst') || 'Sélectionnez un pays d\'abord'}
          </option>
        </select>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label>🏙️ {t('destinationCityLabel')} *</Label>
      <select
        value={selectedCity || ''}
        onChange={(e) => onCityChange(e.target.value)}
        className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
      >
        <option value="">{t('selectCity') || 'Sélectionner une ville'}</option>
        {!cityInList && selectedCity && (
          <option key={selectedCity} value={selectedCity}>{selectedCity}</option>
        )}
        {cities.map((city) => (
          <option key={city.name} value={city.name}>
            {city.name}{city.code ? ` (${city.code})` : ''}
          </option>
        ))}
      </select>
    </div>
  );
};

/** Étape 1 — mode de transport et trajet. */
const RouteStep = ({ form, errors = {}, variant = 'partner', onField, transportModes = ['aerien'] }) => {
  const { t, language } = useLanguage();
  const modeLabels = TRANSPORT_MODE_LABELS[language] || TRANSPORT_MODE_LABELS.fr;

  return (
    <div className="space-y-4">
      {transportModes.length > 1 && (
        <div className="space-y-2">
          <Label>{t('transportMode')}</Label>
          <div className="flex gap-3 flex-wrap">
            {transportModes.map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => onField('transport_mode', mode)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md border-2 transition-all ${
                  form.transport_mode === mode
                    ? 'border-accent bg-accent/10 text-accent'
                    : 'border-border text-muted-foreground hover:border-slate-300'
                }`}
              >
                {mode === 'aerien' ? <Plane className="h-4 w-4" /> : <Ship className="h-4 w-4" />}
                {modeLabels[mode]}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 items-start">
        {variant === 'partner' ? (
          <div className="space-y-1">
            <GeographicSelector
              selectedCountry={form.origin_country}
              onCountryChange={(country) => onField('origin_country', country)}
              mode="origin"
              showCities={false}
              required
            />
            {errors.origin_country && <p className="text-xs text-red-500">{errors.origin_country}</p>}
          </div>
        ) : form.origin_country && (
          // Côté client, l'origine est imposée par le point de dépôt : on la montre
          // sans la rendre modifiable, pour que le trajet reste lisible.
          <div className="space-y-2">
            <Label>{language === 'en' ? 'Departure country' : 'Pays de départ'}</Label>
            <p className="flex h-10 items-center rounded-md border border-border bg-muted/50 px-3 text-sm text-muted-foreground">
              {form.origin_country}
            </p>
          </div>
        )}
        <div className="space-y-1">
          <GeographicSelector
            selectedCountry={form.destination_country}
            onCountryChange={(country) => {
              onField('destination_country', country);
              onField('destination_city', '');
            }}
            showCities={false}
            required
          />
          {errors.destination_country && (
            <p className="text-xs text-red-500">{errors.destination_country}</p>
          )}
        </div>
        <DestinationCitySelect
          selectedCountry={form.destination_country}
          selectedCity={form.destination_city}
          onCityChange={(city) => onField('destination_city', city)}
        />
      </div>

      <p className="text-xs text-muted-foreground">
        {language === 'en'
          ? 'Fields marked with * are required.'
          : 'Les champs marqués d\'un * sont obligatoires.'}
      </p>
    </div>
  );
};

export default RouteStep;
